/**
 * Date-only rendering utilities (friction audit C-9).
 *
 * DOA start/end dates are DATE columns and signature due dates arrive as
 * date-only strings — `new Date('2026-07-25')` parses them as UTC MIDNIGHT,
 * so `toLocaleDateString()` rendered them a day early anywhere west of UTC
 * and "Overdue" flipped at 8pm ET on the due day. A calendar date has no
 * timezone: parse it into LOCAL date parts and compare calendar days.
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** True for '2026-07-25' and UTC-midnight stamps of it ('2026-07-25T00:00:00(.000)?Z'). */
function isDateOnlyLike(value: string): boolean {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return true;
  return /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?(Z|\+00:00)$/.test(value);
}

/**
 * Parse a date-only string (or a UTC-midnight timestamp that encodes one)
 * into a LOCAL Date at local midnight. Real timestamps parse normally.
 * Returns null for empty/invalid input.
 */
export function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  const str = String(value).trim();
  if (isDateOnlyLike(str)) {
    const m = str.match(DATE_ONLY_RE);
    if (m) {
      const parsed = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Render a date-only value like 'Jul 25, 2026' without the UTC day shift. */
export function formatDateOnly(value?: string | null, fallback = '-'): string {
  const date = parseDateOnly(value);
  if (!date) return fallback;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Whole calendar days from today until the value (negative = past). Uses
 * local calendar days, so "due today" stays "due today" until local midnight.
 */
export function daysUntil(value?: string | null): number | null {
  const date = parseDateOnly(value);
  if (!date) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const DAY_MS = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(date) - startOfDay(new Date())) / DAY_MS);
}
